import { useQuery } from '@tanstack/react-query';
import { fetchRiskStatus } from '../api/client';
import ProgressBar from '../components/ProgressBar';

export default function Risk() {
  const { data: riskData, isLoading } = useQuery({
    queryKey: ['risk'],
    queryFn: fetchRiskStatus,
  });

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Risk Monitoring</h1>
        <StatusBadge status={riskData?.status || 'ok'} />
      </div>

      {/* Alerts */}
      {riskData?.alerts && riskData.alerts.length > 0 && (
        <div className="space-y-2">
          {riskData.alerts.map((alert, i) => (
            <div
              key={i}
              className={`p-4 rounded-lg border ${
                riskData.status === 'critical'
                  ? 'bg-danger/10 border-danger/30 text-danger'
                  : 'bg-warning/10 border-warning/30 text-warning'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{riskData.status === 'critical' ? '🚨' : '⚠️'}</span>
                <span>{alert}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Risk Gauges */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Daily P&L Gauge */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Daily P&L vs Limit</h2>
          <div className="space-y-4">
            <GaugeChart
              value={Math.abs(riskData?.dailyPnL.current || 0)}
              max={riskData?.dailyPnL.limit || 500}
              label={
                (riskData?.dailyPnL.current || 0) >= 0
                  ? `+$${riskData?.dailyPnL.current.toFixed(2)}`
                  : `-$${Math.abs(riskData?.dailyPnL.current || 0).toFixed(2)}`
              }
              isLoss={(riskData?.dailyPnL.current || 0) < 0}
            />
            <div className="flex justify-between text-sm text-gray-400">
              <span>Current: ${riskData?.dailyPnL.current.toFixed(2)}</span>
              <span>Limit: ${riskData?.dailyPnL.limit}</span>
            </div>
          </div>
        </div>

        {/* Positions Gauge */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Open Positions</h2>
          <div className="space-y-4">
            <GaugeChart
              value={riskData?.positions.open || 0}
              max={riskData?.positions.max || 5}
              label={`${riskData?.positions.open || 0} / ${riskData?.positions.max || 5}`}
            />
            <div className="flex justify-between text-sm text-gray-400">
              <span>Current: {riskData?.positions.open}</span>
              <span>Max: {riskData?.positions.max}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Cooldown Status */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Cooldown Status</h2>
        {riskData?.cooldown.active ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-2xl">⏱️</span>
              <div>
                <div className="font-medium text-warning">Cooldown Active</div>
                <div className="text-sm text-gray-400">
                  {riskData.cooldown.remainingSeconds}s remaining of {riskData.cooldown.totalSeconds}s
                </div>
              </div>
            </div>
            <ProgressBar
              value={riskData.cooldown.totalSeconds - riskData.cooldown.remainingSeconds}
              max={riskData.cooldown.totalSeconds}
              showValue={false}
            />
          </div>
        ) : (
          <div className="flex items-center gap-3 text-success">
            <span className="text-2xl">✓</span>
            <div>
              <div className="font-medium">No Active Cooldown</div>
              <div className="text-sm text-gray-400">
                System ready for new bets
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Risk Limits Configuration */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Risk Limits</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-surface-50 rounded-lg">
            <div className="text-sm text-gray-400">Max Bet Size</div>
            <div className="text-xl font-bold">${riskData?.limits.maxBetSize}</div>
          </div>
          <div className="p-4 bg-surface-50 rounded-lg">
            <div className="text-sm text-gray-400">Max Daily Loss</div>
            <div className="text-xl font-bold">${riskData?.limits.maxDailyLoss}</div>
          </div>
          <div className="p-4 bg-surface-50 rounded-lg">
            <div className="text-sm text-gray-400">Max Positions</div>
            <div className="text-xl font-bold">{riskData?.limits.maxConcurrentBets}</div>
          </div>
          <div className="p-4 bg-surface-50 rounded-lg">
            <div className="text-sm text-gray-400">Cooldown Period</div>
            <div className="text-xl font-bold">{riskData?.limits.cooldownSeconds}s</div>
          </div>
        </div>
      </div>

      {/* Activity Summary */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Today's Activity</h2>
        <div className="flex items-center gap-8">
          <div>
            <div className="text-3xl font-bold">{riskData?.dailyBets || 0}</div>
            <div className="text-sm text-gray-400">Bets Placed</div>
          </div>
          {riskData?.lastBetTime && (
            <div>
              <div className="text-lg font-medium">
                {formatTime(riskData.lastBetTime)}
              </div>
              <div className="text-sm text-gray-400">Last Bet</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: 'ok' | 'warning' | 'critical' }) {
  const styles = {
    ok: 'bg-success/20 text-success',
    warning: 'bg-warning/20 text-warning',
    critical: 'bg-danger/20 text-danger',
  };

  const labels = {
    ok: 'All Clear',
    warning: 'Warning',
    critical: 'Critical',
  };

  return (
    <span className={`px-4 py-2 rounded-full text-sm font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

function GaugeChart({
  value,
  max,
  label,
  isLoss = false,
}: {
  value: number;
  max: number;
  label: string;
  isLoss?: boolean;
}) {
  const percentage = Math.min((value / max) * 100, 100);
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (percentage / 100) * circumference * 0.75;

  let strokeColor = '#22c55e';
  if (percentage >= 90) {
    strokeColor = '#ef4444';
  } else if (percentage >= 70) {
    strokeColor = '#f59e0b';
  }

  if (isLoss) {
    strokeColor = '#ef4444';
  }

  return (
    <div className="flex flex-col items-center">
      <svg width="150" height="100" viewBox="0 0 150 100">
        {/* Background arc */}
        <path
          d="M 15 85 A 60 60 0 0 1 135 85"
          fill="none"
          stroke="#374151"
          strokeWidth="10"
          strokeLinecap="round"
        />
        {/* Value arc */}
        <path
          d="M 15 85 A 60 60 0 0 1 135 85"
          fill="none"
          stroke={strokeColor}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={circumference * 0.75}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
        />
        {/* Label */}
        <text x="75" y="75" textAnchor="middle" fill="white" fontSize="20" fontWeight="bold">
          {label}
        </text>
      </svg>
      <div className="text-sm text-gray-400 mt-2">
        {percentage.toFixed(0)}% of limit
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 w-48 bg-surface-50 rounded"></div>
      <div className="grid grid-cols-2 gap-6">
        <div className="h-48 bg-surface-50 rounded-lg"></div>
        <div className="h-48 bg-surface-50 rounded-lg"></div>
      </div>
      <div className="h-32 bg-surface-50 rounded-lg"></div>
    </div>
  );
}

function formatTime(isoString: string): string {
  return new Date(isoString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}
